import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { logout } from "@containers/Auth/store/actions";
import { userStorage } from "@shared/utils";
import { BaseQueryFn } from "@reduxjs/toolkit/query";

import { store } from "../../index";
import { showHttpErrorNotification } from "./functions";

axios.defaults.headers.post["Content-Type"] = "application/json";

const axiosInstance: AxiosInstance = axios.create({
  withCredentials: true,
});

const makeRequest = (instance: AxiosInstance) => (method: string, url: string, params: unknown[]) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return instance[method](url, ...params);
};

/**
 * @deprecated will be removed in next version
 */
axiosInstance.interceptors.request.use((config) => {
  const token = userStorage.getToken();

  if (token) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }

  config.headers = { ...config.headers, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };

  return config;
});

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const response = res.data || res;
    if (response.error) {
      return Promise.reject(response.error);
    }
    return response;
  },
  (error: AxiosError) => {
    const { data, config } = error?.response as {
      data?: { unauthorized: boolean; code: number };
      config?: { url: string };
    };

    if (data) {
      if (!config?.url?.includes("/auth/")) {
        const unauthorized: boolean = data?.unauthorized || data?.code === 401;

        if (unauthorized) {
          store.dispatch(showHttpErrorNotification(data));
          store.dispatch(logout.request({ withApiRequest: false }));
        }
      }

      return Promise.reject(data);
    }

    return Promise.reject(error);
  },
);
/**
 * Axios wrapper
 *
 * @param  {string} method Method of the request
 * @param  {string} url url of the request
 *
 * @return {object} wrapped axios function that receives params
 */
export default (method: string, url: string) =>
  (...params: unknown[]) =>
    makeRequest(axiosInstance)(method, url, params);

export const download = async (url: string, fileName?: string, onError?: (e?: Event) => void) => {
  if (fileName) {
    const link = document.createElement("a");
    link.href = url;
    fileName && link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } else {
    // TODO: it's a hack for downloading multiple files
    // There is no way to provide a file name with an iframe
    // We should refactor it in the feature, probably split downloading by single and multiple files
    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-downloads allow-scripts");
    iframe.src = url;
    iframe.setAttribute("style", "display: none");
    if (onError) iframe.onload = onError;
    document.body.appendChild(iframe);
  }
};

export const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: "" },
): BaseQueryFn<
  {
    url: string;
    method?: AxiosRequestConfig["method"];
    body?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
    headers?: AxiosRequestConfig["headers"];
    responseType?: AxiosRequestConfig["responseType"];
  },
  unknown,
  unknown
> => {
  return async ({ url, method, body, params, headers, responseType }) => {
    try {
      const result = await axiosInstance({
        url: baseUrl + url,
        method,
        data: body,
        params,
        headers,
        responseType,
      });

      return { data: result };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      const status = err.response?.status || err.code || 500;
      const data = err.response?.data || err.message || "";

      return {
        error: { status, data, raw: err },
        code: status,
        message: data,
      };
    }
  };
};
